/**
 * @module modules/useClickOutside
 * @description React hook to detect and handle clicks outside of a specific element.
 * Useful for closing dropdowns, modals, or other UI elements when clicking outside.
 */

import { useEffect, useRef } from 'react';

/**
 * @function useClickOutside
 * @description React hook that triggers a callback when a click occurs outside the referenced element.
 * Uses event capture phase to detect clicks before they bubble up. Automatically cleans up
 * event listeners on unmount or when dependencies change.
 * @param {Function} onClick - Callback function to execute when click occurs outside element
 * @param {boolean} stopPropagation - Whether to stop event propagation and prevent default behavior
 * @returns {React.RefObject<HTMLElement>} React ref to attach to the element to monitor
 */
const useClickOutside = (onClick, stopPropagation) => {
  const el = useRef(null);

  useEffect(() => {
    const listener = (event) => {
      if (el.current && !el.current.contains(event.target)) {
        if (stopPropagation) {
          event.stopPropagation();
          event.preventDefault();
        }
        onClick(event);
      }
    };

    /* listens to the capture phase */
    document.addEventListener('click', listener, { capture: true });

    return () => {
      document.removeEventListener('click', listener, { capture: true });
    };
  }, [onClick, stopPropagation]);

  return el;
};

export default useClickOutside;
